import { Combobox, createListCollection, Portal } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

interface ComboboxItem {
  label: string;
  value: string;
  [key: string]: any; // Allow additional properties
}

interface ComboboxProps {
  items: ComboboxItem[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  loading?: boolean;
  disablePortal?: boolean; // render dropdown inline (helps on mobile/drawers)
}

export default function CustomCombobox({
  items,
  value,
  onValueChange,
  placeholder = 'Select an option',
  label,
  loading = false,
  disablePortal = false,
}: ComboboxProps) {
  const [inputValue, setInputValue] = useState('');

  const filteredItems = useMemo(() => {
    const normalized = inputValue.toLowerCase();
    if (!normalized) return items;
    return items.filter((item) => item.label.toLowerCase().includes(normalized));
  }, [items, inputValue]);

  const collection = useMemo(
    () =>
      createListCollection({
        items: filteredItems,
      }),
    [filteredItems],
  );

  const handleInputValueChange = (e: any) => {
    setInputValue(e.inputValue ?? '');
  };

  const handleValueChange = (e: any) => {
    if (onValueChange) {
      // If value array is empty (clear button clicked), pass empty string
      // Otherwise pass the first selected value
      onValueChange(e.value.length > 0 ? e.value[0] : '');
    }
  };

  const Content = (
    <Combobox.Positioner style={{ zIndex: 1000 }}>
      <Combobox.Content>
        <Combobox.Empty>No items found</Combobox.Empty>
        {collection.items.map((item, index) => (
          <Combobox.Item key={`${item.value}-${index}`} item={item}>
            {item.label}
            <Combobox.ItemIndicator />
          </Combobox.Item>
        ))}
      </Combobox.Content>
    </Combobox.Positioner>
  );

  return (
    <Combobox.Root
      collection={collection}
      value={value ? [value] : []}
      onValueChange={handleValueChange}
      onInputValueChange={handleInputValueChange}
    >
      <Combobox.Label>{label}</Combobox.Label>
      <Combobox.Control>
        <Combobox.Input placeholder={loading ? 'Loading...' : placeholder} />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      {disablePortal ? Content : (
        <Portal>
          {Content}
        </Portal>
      )}
    </Combobox.Root>
  );
}
